import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import { v4 as uuidv4 } from 'uuid';
import { Metric } from '@/components/companies/MetricsTable';

/**
 * Delete a pricing metric from the database
 * 
 * @param metricId The ID of the metric to delete
 * @param companySlug The company slug
 * @param pageSubslug Optional subslug for the specific page
 * @returns Promise containing the result of the operation
 */ export const deletePricingMetric = async (
  metricId: string,
  companySlug: string,
  pageSubslug?: string
) => {
  if (!metricId) {
    return { success: false, message: "No metric ID provided" };
  }

  if (!companySlug) {
    return { success: false, message: "No company slug provided" };
  }

  try {
    console.log(`🗑️ Deleting metric ${metricId} for ${companySlug}${pageSubslug ? `/${pageSubslug}` : ''}`);
    
    // 1. First, get the metric name for better logging
    const { data: metricData, error: metricError } = await supabase
      .from('page_pricing_metrics')
      .select('display_name, name')
      .eq('id', metricId)
      .single();
    
    if (metricError) {
      console.error('Error fetching metric data:', metricError);
      // Continue with deletion even if we can't get the name
    }

    // Handle potential type errors by using type assertion
    const metricName = metricData && typeof metricData === 'object' ? 
      (metricData as any).display_name || (metricData as any).name || metricId : metricId;

    // Use the edge function we already have for deleting metrics
    // This function properly handles all the foreign key constraints
    console.log(`Calling delete-metric edge function to handle dependencies...`);
    
    try {
      // Get session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      // Create the request URL for the edge function
      const url = new URL('/functions/v1/delete-metric', 'https://proxy.buyersonserver.com');
      
      // Make the request to the edge function
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          metricId,
          companySlug,
          pageSubslug
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete metric');
      }
      
      const result = await response.json();
      
      // Log the successful deletion
      console.log(`Successfully deleted metric '${metricName}' (${metricId})`);
      
      return {
        success: result.success,
        message: result.message || `Successfully deleted metric "${metricName}"`
      };
    } catch (deleteError) {
      // If the RPC function is not available, fall back to manual deletion
      console.warn('RPC function not available, falling back to manual deletion', deleteError);
      
      // 2. Manual deletion process - delete all references first
      // Delete from page_pricing_metrics_map first to avoid FK constraints
      console.log(`Removing mappings for metric ${metricId}...`);
      
      const { error: mapDeleteError } = await supabase
        .from('page_pricing_metrics_map')
        .delete()
        .eq('page_pricing_metrics_id', metricId);
      
      if (mapDeleteError) {
        console.warn('Warning: Failed to remove mappings:', mapDeleteError);
        // Continue anyway
      }
      
      // 3. Finally delete the metric itself
      console.log(`Deleting metric ${metricId}...`);
      const { error: metricDeleteError } = await supabase
        .from('page_pricing_metrics')
        .delete()
        .eq('id', metricId);
      
      if (metricDeleteError) {
        console.error('Error deleting metric:', metricDeleteError);
        return {
          success: false,
          message: `Failed to delete metric: ${metricDeleteError.message}`
        };
      }
      
      // Log the successful deletion
      console.log(`Successfully deleted metric '${metricName}' (${metricId})`);
      
      return {
        success: true,
        message: `Successfully deleted metric "${metricName}"`
      };
    }
  } catch (error) {
    console.error('❌ Error in deletePricingMetric:', error);
    
    return { 
      success: false, 
      message: `Error deleting metric: ${error instanceof Error ? error.message : String(error)}` 
    };
  }
};

/**
 * Add a new pricing metric to the database
 * 
 * @param companySlug The company slug
 * @param name The name of the metric
 * @param modality The modality of the metric (Flat, License, Credit, Usage, Threshold, Unknown)
 * @param sampleValues Optional array of sample values for the metric
 * @param samplePrices Optional array of sample prices for the metric
 * @param pageSubslug Optional subslug for the specific page
 * @returns Promise containing the result of the operation and the new metric ID
 */
export const addMetric = async (
  companySlug: string,
  name: string,
  modality: 'Flat' | 'License' | 'Credit' | 'Usage' | 'Threshold' | 'Unknown',
  sampleValues: string[] = [],
  samplePrices: string[] = [],
  pageSubslug?: string
): Promise<{ success: boolean; message: string; id?: string }> => {
  if (!companySlug) {
    return { success: false, message: "No company slug provided" };
  }

  if (!name || name.trim() === '') {
    return { success: false, message: "No metric name provided" };
  }

  try {
    const slug = pageSubslug ? `${companySlug}/${pageSubslug}` : companySlug;
    console.log(`🆕 Adding new metric '${name}' with modality '${modality}' for ${slug}`);
    
    // Generate a unique ID for the new metric
    const metricId = uuidv4();
    
    // Create the new metric entry in page_pricing_metrics table
    const { data: newMetric, error: insertError } = await supabase
      .from('page_pricing_metrics')
      .insert({
        id: metricId,
        slug,
        display_name: name,
        modality,
        sample_values: sampleValues.length > 0 ? sampleValues : null,
        sample_prices: samplePrices.length > 0 ? samplePrices : null,
        quarter: new Date().toISOString().substring(0, 7).replace('-', 'Q') // Format as YYYYQMM
      })
      .select()
      .single();
    
    if (insertError) {
      console.error('Error creating metric:', insertError);
      return { 
        success: false, 
        message: `Failed to create metric: ${insertError.message}` 
      };
    }

    console.log(`✅ Successfully created metric '${name}' (${metricId})`);
    
    return { 
      success: true, 
      message: `Successfully created metric "${name}"`,
      id: metricId
    };
  } catch (error) {
    console.error('❌ Error in addMetric:', error);
    
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'An unknown error occurred';
    
    return { 
      success: false, 
      message: `Error creating metric: ${errorMessage}` 
    };
  }
};

/**
 * Update a pricing metric in the database
 * 
 * @param metricId The ID of the metric to update
 * @param updateData The data to update (display_name, modality)
 * @returns Promise containing the result of the operation
 */
export const updatePricingMetric = async (
  metricId: string,
  updateData: {
    display_name?: string;
    modality?: string;
  }
): Promise<{ success: boolean; message: string; updatedMetric?: any }> => {
  if (!metricId) {
    return { success: false, message: "No metric ID provided" };
  }

  try {
    console.log(`🔄 Updating metric ${metricId} with data:`, updateData);
    
    // Create update object with only the fields that are provided
    const updateObject: any = {};
    
    if (updateData.display_name !== undefined) {
      updateObject.display_name = updateData.display_name;
    }
    
    if (updateData.modality !== undefined) {
      updateObject.modality = updateData.modality;
    }
    
    // Update the metric in the database
    const { data: updatedMetric, error: updateError } = await supabase
      .from('page_pricing_metrics')
      .update(updateObject)
      .eq('id', metricId)
      .select('*')
      .single();
    
    if (updateError) {
      console.error('Error updating metric:', updateError);
      return { 
        success: false, 
        message: `Failed to update metric: ${updateError.message}` 
      };
    }
    
    console.log(`✅ Successfully updated metric ${metricId}`, updatedMetric);
    
    return { 
      success: true, 
      message: 'Metric updated successfully',
      updatedMetric
    };
  } catch (error) {
    console.error('❌ Error in updatePricingMetric:', error);
    
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'An unknown error occurred';
    
    return { 
      success: false, 
      message: `Error updating metric: ${errorMessage}` 
    };
  }
};
